// Mini-sprint 6-action-plan — блок «Что можно по питанию» внутри карточки биомаркера.
//
// Рендерится ТОЛЬКО когда есть verified food-rule под текущее состояние маркера
// (status==='ready'). При disabled / no_data / no_advice / loading / error → null
// (вспомогательный блок, не показываем пустых состояний).
//
// Контент — образовательные пищевые подсказки из проверенной rule-library
// (не AI). Обязательный дисклеймер рядом (Biomarker_Analytics_Decisions §8).

import { Utensils } from 'lucide-react';
import { useBiomarkerActionPlan } from '@/api/hooks/actionPlan';
import type { ActionPlanResponse } from '@/api/types';

/**
 * Pure-helper (тестируется без рендера, как BiomarkerInsightBlock helpers).
 * Возвращает контент к показу или null, если показывать нечего: не ready,
 * либо оба food-поля пустые.
 */
export function resolveActionPlan(
  data: ActionPlanResponse | undefined,
): { foodAdd: string | null; foodLimit: string | null; permanentDisclaimer: string } | null {
  if (!data || data.status !== 'ready') return null;
  const { food_add: foodAdd, food_limit: foodLimit } = data.payload;
  if (!foodAdd && !foodLimit) return null;

  return { foodAdd, foodLimit, permanentDisclaimer: data.permanent_disclaimer };
}

export function ActionPlanBlock({ code }: { code: string }) {
  const { data } = useBiomarkerActionPlan(code);

  const content = resolveActionPlan(data);
  if (content === null) return null;

  const { foodAdd, foodLimit } = content;

  return (
    <section className="rounded-surface border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
          <Utensils className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
        </div>
        <h2 className="text-sm font-semibold text-emerald-800">Что можно по питанию</h2>
      </div>

      {foodAdd && (
        <div className="mb-3">
          <span className="mb-1 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-hl-small font-semibold text-emerald-700">
            Чаще добавлять
          </span>
          <p className="whitespace-pre-line text-hl-secondary leading-relaxed text-foreground">{foodAdd}</p>
        </div>
      )}

      {foodLimit && (
        <div>
          <span className="mb-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-hl-small font-semibold text-amber-700">
            Ограничить
          </span>
          <p className="whitespace-pre-line text-hl-secondary leading-relaxed text-foreground">{foodLimit}</p>
        </div>
      )}

      {content.permanentDisclaimer && (
        <p className="mt-4 text-hl-small leading-relaxed text-muted-foreground">
          {content.permanentDisclaimer}
        </p>
      )}
    </section>
  );
}
